// TODO: Remove this when fixing the unit tests - BTCOBUI-2597
/* istanbul ignore file */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import RBush from 'rbush';
// eslint-disable-next-line import/no-extraneous-dependencies
import knn from 'rbush-knn';
import { usePrevious } from '../../../hooks/usePrevious';
import { MarkerDetails } from './Map';
import { createMarkerEl, getMinimumUnclusteredZoom } from './util';

const MARKER_SIZE = 40;

const createMarker = ({
  markerId,
  latitude,
  longitude,
  isSelected,
  onClick,
}: {
  markerId: string;
  latitude: number;
  longitude: number;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const marker = new google.maps.marker.AdvancedMarkerElement({
    position: { lat: latitude, lng: longitude },
    content: createMarkerEl({
      width: MARKER_SIZE,
      height: MARKER_SIZE,
      isSelected: isSelected,
    }),
  });

  marker.id = markerId;

  marker.addListener('click', onClick);

  return marker;
};

type TreeMarkerItem = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  data: MarkerDetails;
};

type Props = {
  zoom: number;
  selectedMarkerId: string | null;
  markers: MarkerDetails[];
  onLoaded: () => void;
  onMarkerClick: (markerId: string) => void;
};

export const GoogleMapView = ({ zoom, selectedMarkerId, markers, onLoaded, onMarkerClick }: Props) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markerClustererRef = useRef<MarkerClusterer | null>(null);
  const markerElsRef = useRef<google.maps.marker.AdvancedMarkerElement[] | null>(null);
  const markersRTreeRef = useRef<RBush<TreeMarkerItem> | null>(null);
  const prevSelectedMarkerId = usePrevious(selectedMarkerId);

  const updateMarkersContents = useCallback(() => {
    const markerClusterer = markerClustererRef.current;
    const markerEls = markerElsRef.current;

    if (!markerClusterer || !markerEls) return;

    const newMarkers = markerEls.reduce((ac, marker) => {
      if (selectedMarkerId !== marker.id && prevSelectedMarkerId !== marker.id) {
        return [...ac, marker];
      }

      let latNum = 0;
      let lngNum = 0;

      const lat = marker.position?.lat;
      const lng = marker.position?.lng;

      if (lat) latNum = typeof lat === 'number' ? lat : lat();
      if (lng) lngNum = typeof lng === 'number' ? lng : lng();

      const newMarker = createMarker({
        markerId: marker.id,
        isSelected: selectedMarkerId === marker.id,
        latitude: latNum,
        longitude: lngNum,
        onClick: () => {
          onMarkerClick(marker.id);
        },
      });

      markerClusterer.removeMarker(marker);
      markerClusterer.addMarker(newMarker);

      return [...ac, newMarker];
    }, [] as google.maps.marker.AdvancedMarkerElement[]);

    markerElsRef.current = newMarkers;
  }, [markers, onMarkerClick, selectedMarkerId, prevSelectedMarkerId]);

  const panToMarker = useCallback(
    (markerId: string) => {
      const markersRTree = markersRTreeRef.current;

      if (!map || !markersRTree) return;

      const selectedMarker = markers.find((marker) => marker.id === markerId);

      if (!selectedMarker) return;

      const closest = knn(markersRTree, selectedMarker.longitude, selectedMarker.latitude, 2);

      if (closest.length < 2) {
        map.panTo({ lat: selectedMarker.latitude, lng: selectedMarker.longitude });
        return;
      }

      const requiredZoom = getMinimumUnclusteredZoom(
        { lat: closest[0].minY, lng: closest[0].minX },
        { lat: closest[1].minY, lng: closest[1].minX },
      );

      const currentZoom = map.getZoom();

      if (currentZoom != undefined && currentZoom < requiredZoom) {
        map.setZoom(requiredZoom);
      }

      map.panTo({ lat: selectedMarker.latitude, lng: selectedMarker.longitude });
    },
    [map, markers],
  );

  const initMap = useCallback(async () => {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const mapContainerEl = document.getElementById('google-map-container');

    if (!mapContainerEl) return;

    const mapInstance = new Map(mapContainerEl, {
      zoom: zoom,
      mapId: 'STORE_LOCATOR_MAP',
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      zoomControl: true,
      cameraControl: false,
    });

    setMap(mapInstance);
    onLoaded();
  }, [onLoaded, setMap]);

  const renderMarkers = useCallback(() => {
    if (!map) return;

    const AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

    let markerClusterer = markerClustererRef.current;

    if (!markerClusterer) {
      markerClusterer = new MarkerClusterer({
        map,
        renderer: {
          render: ({ position, count }) => {
            return new AdvancedMarkerElement({
              position: position,

              content: createMarkerEl({
                width: MARKER_SIZE,
                height: MARKER_SIZE,
                isCluster: true,
                count,
              }),
            });
          },
        },
      });

      markerClustererRef.current = markerClusterer;
    }

    markerClusterer.clearMarkers();

    const advancedMarkers = markers.map((position) => {
      const marker = createMarker({
        markerId: position.id,
        isSelected: false,
        latitude: position.latitude,
        longitude: position.longitude,
        onClick: () => {
          onMarkerClick(position.id);
        },
      });

      return marker;
    });

    markerElsRef.current = advancedMarkers;

    markerClusterer.addMarkers(advancedMarkers);

    const bounds = new google.maps.LatLngBounds();
    markers.forEach(({ latitude, longitude }) => {
      return bounds.extend(new google.maps.LatLng(latitude, longitude));
    });

    map.fitBounds(bounds);
  }, [map, markers]);

  useEffect(() => {
    initMap();
  }, []);

  useEffect(() => {
    const rTree = new RBush<TreeMarkerItem>();
    rTree.load(
      markers.map((marker) => ({
        minX: marker.longitude,
        minY: marker.latitude,
        maxX: marker.longitude,
        maxY: marker.latitude,
        data: marker,
      })),
    );

    markersRTreeRef.current = rTree;

    renderMarkers();
  }, [map, markers, renderMarkers]);

  useEffect(() => {
    if (!map || !selectedMarkerId) return;

    updateMarkersContents();
    panToMarker(selectedMarkerId);
  }, [map, selectedMarkerId, updateMarkersContents, panToMarker]);

  return <div id="google-map-container" style={{ width: '100%', height: '100%' }}></div>;
};
